import { Button, cn } from "@etudescarre/ui";
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useRef } from "react";
import type { DocumentData, LotData } from "./document-table/types";

// Shared by the study progress screens: by zone (012, 025, 034) and by date
// (014, 027). The document table needs the whole width at 1280 px: the
// buildings sit above the zones, and the table of a date opens over its list.

type InterventionDocuments = {
  lot: { id: number; name: string; nLot: string | null };
  performance: { id: number; name: string };
  documents: DocumentData[];
};

/** One intervention as the lots of the document table. */
export function interventionLots(item: InterventionDocuments): LotData[] {
  return [
    {
      id: item.lot.id,
      name: item.lot.name,
      nLot: item.lot.nLot,
      prestas: [
        {
          id: item.performance.id,
          name: item.performance.name,
          documents: item.documents,
        },
      ],
    },
  ];
}

export type BuildingNavProps = {
  buildings: Array<{ id: number; name: string; shortName: string | null }>;
  selectedId: number | undefined;
  onSelect: (buildingId: number) => void;
};

export function BuildingNav({
  buildings,
  selectedId,
  onSelect,
}: BuildingNavProps) {
  return (
    <nav
      aria-label="Bâtiments"
      className="flex max-h-16 shrink-0 flex-wrap gap-1 overflow-y-auto"
    >
      {buildings.map((item) => (
        <button
          key={item.id}
          type="button"
          title={item.name}
          aria-current={item.id === selectedId ? "true" : undefined}
          onClick={() => onSelect(item.id)}
          className={cn(
            "max-w-48 truncate rounded-lg px-2.5 py-1 text-sm",
            item.id === selectedId
              ? "bg-blue-600 text-white"
              : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-100",
          )}
        >
          {item.shortName ?? item.name}
        </button>
      ))}
    </nav>
  );
}

export type ZoneCardProps = {
  name: string;
  counters: string[];
  open: boolean;
  onToggle: () => void;
};

export function ZoneCard({ name, counters, open, onToggle }: ZoneCardProps) {
  const cardRef = useRef<HTMLButtonElement>(null);

  // A building can list dozens of zones: without this, a zone opened at the
  // bottom of the window would unfold below it.
  useEffect(() => {
    if (open) cardRef.current?.scrollIntoView({ block: "start" });
  }, [open]);

  return (
    <button
      ref={cardRef}
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="w-full rounded-lg border border-zinc-200 bg-white p-4 text-left dark:border-zinc-700 dark:bg-zinc-900"
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-zinc-900 dark:text-zinc-100">
          {name}
        </span>
        {open ? <CaretDownIcon /> : <CaretRightIcon />}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-600 dark:text-zinc-400">
        {counters.map((counter) => (
          <span key={counter}>{counter}</span>
        ))}
      </div>
    </button>
  );
}

export type DocumentsPanelProps = {
  title: string;
  onClose: () => void;
  children: ReactNode;
};

/** Covers its `relative` parent, like the panel of mockups 014 and 027. */
export function DocumentsPanel({
  title,
  onClose,
  children,
}: DocumentsPanelProps) {
  return (
    <section
      aria-label={title}
      className="absolute inset-0 z-20 flex flex-col gap-3 bg-zinc-50 dark:bg-zinc-950"
    >
      <div className="flex shrink-0 items-center justify-between gap-3">
        <h3 className="min-w-0 truncate font-semibold text-zinc-900 dark:text-zinc-100">
          {title}
        </h3>
        <Button onClick={onClose} className="shrink-0 text-sm">
          Fermer
        </Button>
      </div>
      {children}
    </section>
  );
}
